import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { validatePassword } from '@hygh-vybe/lib';
import './auth.css';
import { API_BASE } from '../../config';

export function ResetConfirmPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldError('');

    const passwordResult = validatePassword(password);
    if (!passwordResult.success) {
      setFieldError(passwordResult.errors[0]?.reason || 'Invalid password');
      return;
    }

    if (!code || !email) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/reset-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword: password }),
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error?.message || 'Password reset failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>Password reset</h1>
          <div className="auth-success" style={{ marginBottom: 'var(--spacing-lg)' }}>
            Your password has been reset successfully.
          </div>
          <div className="auth-footer">
            <Link to="/auth/login">Sign in with your new password</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Set new password</h1>
        <p className="auth-subtitle">Enter your new password below</p>

        {error && <div className="auth-error" role="alert">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8+ chars, uppercase, lowercase, number"
              className={fieldError ? 'field-error' : ''}
              required
            />
            {fieldError && <span className="field-error-msg">{fieldError}</span>}
          </div>

          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/auth/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
